import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { Utente } from '../types';

// ── Tipi ──────────────────────────────────────────────────────────────────────

interface ModuloFlags {
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canExport: boolean;
}

// permMap[societaId][modulo] = { canView, canEdit, canDelete, canExport }
type PermMap = Record<number, Record<string, ModuloFlags>>;

type Azione = 'view' | 'edit' | 'delete' | 'export';

interface PermessiCtx {
  utente: Utente | null;
  loading: boolean;
  /** Controlla se l'utente ha il permesso su un modulo.
   *  - societaId fornito → vera solo per quella società specifica
   *  - societaId omesso   → vera solo per la SOCIETÀ ATTIVA in topbar
   *
   *  ⚠️ Non fa mai l'unione dei permessi di tutte le società: un operativo
   *  lavora sempre dentro una sola società e deve vedere solo ciò che quella
   *  società gli concede (vedi CLAUDE.md §1.2).
   */
  can: (modulo: string, azione?: Azione, societaId?: number) => boolean;
  isSuperadmin: boolean;
  /** Flag `Utente.gestisceAssistenza`: è lo staff dei ticket di assistenza.
   *  Non coincide più con SUPERADMIN (vedi backend/src/utils/staffAssistenza.js). */
  gestisceAssistenza: boolean;
  permMap: PermMap;
  /** Società di contesto usata da `can` quando non viene passato un societaId. */
  societaContestoId: number | null;
  /** Registra la società attiva. Chiamata da SocietaProvider — non usare altrove. */
  setSocietaContesto: (societaId: number | null, pronta: boolean) => void;
}

// ── Context ───────────────────────────────────────────────────────────────────

const PermessiContext = createContext<PermessiCtx>({
  utente: null,
  loading: true,
  can: () => false,
  isSuperadmin: false,
  gestisceAssistenza: false,
  permMap: {},
  societaContestoId: null,
  setSocietaContesto: () => {},
});

// ── Helper ────────────────────────────────────────────────────────────────────

/**
 * Moduli volutamente cross-società: la loro visibilità non dipende dalla società
 * attiva in topbar perché i dati non sono partizionati per società.
 * Contropartita frontend delle route con `societaObbligatoria: false`:
 *  - messaggistica → CLAUDE.md §1.7 (l'operativo riceve messaggi da tutte le sue società)
 *  - richieste assistenza → visibilità per utente, non per società
 * Per questi `can()` resta l'unione: basta una società che li conceda.
 */
const MODULI_CROSS_SOCIETA = new Set([
  'aziendale.messaggi_lettura',
  'aziendale.messaggi_invio',
  'assistenza.richieste',
  // le mie attivita di adozione: assegnate alla persona, non alla societa attiva
  'adozione.mie_attivita',
]);

/** Moduli che il flag "gestisce assistenza" apre da solo, senza permesso assegnato. */
const MODULI_ASSISTENZA = new Set([
  'assistenza.richieste',
  'assistenza.knowledge_base',
]);

function azioneToField(azione: Azione): keyof ModuloFlags {
  const map: Record<Azione, keyof ModuloFlags> = {
    view:   'canView',
    edit:   'canEdit',
    delete: 'canDelete',
    export: 'canExport',
  };
  return map[azione];
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function PermessiProvider({ children }: { children: React.ReactNode }) {
  const [utente,  setUtente]  = useState<Utente | null>(null);
  const [permMap, setPermMap] = useState<PermMap>({});
  const [loading, setLoading] = useState(true);

  // Società di contesto: la pusha SocietaProvider (che è figlio di questo
  // provider e quindi non può essere letto da qui con un hook). Finché
  // `pronta` è false il contesto non è ancora determinato.
  const [societaContesto, setSocietaContestoState] =
    useState<{ id: number | null; pronta: boolean }>({ id: null, pronta: false });

  // Stabile: è dependency dell'effect in SocietaProvider che la richiama.
  const setSocietaContesto = useCallback((societaId: number | null, pronta: boolean) => {
    setSocietaContestoState(prev =>
      (prev.id === societaId && prev.pronta === pronta) ? prev : { id: societaId, pronta }
    );
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }

    let cancelled = false;

    apiFetch<{ utente: Utente }>('/auth/me')
      .then(async d => {
        if (cancelled) return;
        const u = d.utente;
        setUtente(u);

        if (u.ruolo === 'SUPERADMIN') {
          // SUPERADMIN: non serve caricare permessi dal DB
          setLoading(false);
          return;
        }

        // Per ogni società carica i permessi e li mette in mappa
        const societies = u.societa ?? [];
        const map: PermMap = {};

        await Promise.all(
          societies.map(async s => {
            try {
              const data = await apiFetch<{
                sezioni: { sezione: string; moduli: (ModuloFlags & { modulo: string; label: string })[] }[];
              }>(`/api/permessi/me?societaId=${s.id}`);

              const socMap: Record<string, ModuloFlags> = {};
              for (const sezione of data.sezioni) {
                for (const m of sezione.moduli) {
                  socMap[m.modulo] = {
                    canView:   m.canView,
                    canEdit:   m.canEdit,
                    canDelete: m.canDelete,
                    canExport: m.canExport,
                  };
                }
              }
              map[s.id] = socMap;
            } catch (_) {
              // Se la fetch fallisce, la società non contribuisce
              map[s.id] = {};
            }
          })
        );

        if (!cancelled) {
          setPermMap(map);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const isSuperadmin = utente?.ruolo === 'SUPERADMIN';
  const gestisceAssistenza = !!utente?.gestisceAssistenza;

  const can = (modulo: string, azione: Azione = 'view', societaId?: number): boolean => {
    if (!utente) return false;
    if (utente.ruolo === 'SUPERADMIN') return true;

    // Lo staff dell'assistenza entra nel modulo anche senza permesso assegnato:
    // il flag basta da solo (stessa regola del backend, routes/assistenza.js).
    if (gestisceAssistenza && MODULI_ASSISTENZA.has(modulo)) return true;

    const field = azioneToField(azione);

    // Società esplicita (pulsanti CRUD di una riga, liste multi-società)
    if (societaId != null) {
      return !!permMap[societaId]?.[modulo]?.[field];
    }

    // Caso normale: si applicano i permessi della società attiva in topbar.
    if (societaContesto.pronta && !MODULI_CROSS_SOCIETA.has(modulo)) {
      if (societaContesto.id == null) return false; // operativo senza società → nega
      return !!permMap[societaContesto.id]?.[modulo]?.[field];
    }

    // Unione fra tutte le società. Due casi:
    //  - modulo cross-società (vedi MODULI_CROSS_SOCIETA): è il comportamento voluto
    //  - contesto non ancora determinato (primo render, o provider usato fuori da
    //    SocietaProvider): finestra di pochi millisecondi in cui il menu resta
    //    permissivo — il backend resta comunque l'autorità sui dati.
    return Object.values(permMap).some(socMap => !!socMap[modulo]?.[field]);
  };

  return (
    <PermessiContext.Provider value={{
      utente, loading, can, isSuperadmin, gestisceAssistenza, permMap,
      societaContestoId: societaContesto.id,
      setSocietaContesto,
    }}>
      {children}
    </PermessiContext.Provider>
  );
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

export function usePermesso() {
  return useContext(PermessiContext);
}

export function useSuperadmin(): boolean {
  return useContext(PermessiContext).isSuperadmin;
}
